// SVG rendering utilities

import { getConnectorEndStyleSvg } from './renderingUtils';

// Generate SVG marker definitions for arrow heads
const generateSvgMarkers = (): string => {
  return `
    <defs>
      <marker id="pointy-arrow" viewBox="0 0 10 10" markerWidth="6" markerHeight="6" refX="8" refY="5" orient="auto-start-reverse" markerUnits="strokeWidth">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" stroke="none" stroke-width="1"/>
      </marker>
      <marker id="filled-arrow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
        <path d="M0,0 L0,6 L9,3 z" fill="context-stroke"/>
      </marker>
      <marker id="filled-circle" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto" markerUnits="strokeWidth">
        <circle cx="4" cy="4" r="3" fill="context-stroke"/>
      </marker>
    </defs>
  `;
};

// Render whiteboard shape to SVG
const renderWhiteboardShape = (obj: any): string => {
  const {
    left,
    top,
    width,
    height,
    shapeName,
    fill,
    stroke,
    strokeWidth,
    textContent,
  } = obj;

  let shapeElement = '';
  let textElement = '';

  // Create the shape element based on shapeName
  switch (shapeName) {
    case 'rect': {
      shapeElement = `<rect x="${left}" y="${top}" width="${width}" height="${height}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
      break;
    }
    case 'roundRect': {
      shapeElement = `<rect x="${left}" y="${top}" width="${width}" height="${height}" rx="8" ry="8" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
      break;
    }
    case 'circle': {
      const radius = Math.min(width, height) / 2;
      const cx = left + width / 2;
      const cy = top + height / 2;
      shapeElement = `<circle cx="${cx}" cy="${cy}" r="${radius}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
      break;
    }
    case 'diamond': {
      const centerX = left + width / 2;
      const centerY = top + height / 2;
      const points = `${centerX},${top} ${left + width},${centerY} ${centerX},${
        top + height
      } ${left},${centerY}`;
      shapeElement = `<polygon points="${points}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
      break;
    }
    case 'triangle': {
      const trianglePoints = `${left + width / 2},${top} ${left + width},${
        top + height
      } ${left},${top + height}`;
      shapeElement = `<polygon points="${trianglePoints}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
      break;
    }
    case 'parallelogram': {
      const offset = width * 0.2;
      const paraPoints = `${left + offset},${top} ${left + width},${top} ${
        left + width - offset
      },${top + height} ${left},${top + height}`;
      shapeElement = `<polygon points="${paraPoints}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
      break;
    }
    case 'processEnd': {
      const d = `M ${left + height / 2} ${top} L ${
        left + width - height / 2
      } ${top} A ${height / 2} ${height / 2} 0 0 1 ${
        left + width - height / 2
      } ${top + height} L ${left + height / 2} ${top + height} A ${
        height / 2
      } ${height / 2} 0 0 1 ${left + height / 2} ${top} Z`;
      shapeElement = `<path d="${d}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
      break;
    }
    case 'cylinder': {
      const ellipseHeight = height * 0.1;
      // Format as a single line to avoid prettier formatting issues
      shapeElement = `<ellipse cx="${left + width / 2}" cy="${
        top + ellipseHeight / 2
      }" rx="${width / 2}" ry="${
        ellipseHeight / 2
      }" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/><rect x="${left}" y="${
        top + ellipseHeight / 2
      }" width="${width}" height="${
        height - ellipseHeight
      }" fill="${fill}" stroke="none"/><rect x="${left}" y="${
        top + ellipseHeight / 2
      }" width="${width}" height="${
        height - ellipseHeight
      }" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}"/><ellipse cx="${
        left + width / 2
      }" cy="${top + height - ellipseHeight / 2}" rx="${width / 2}" ry="${
        ellipseHeight / 2
      }" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
      break;
    }
    case 'customPath': {
      // For custom path shapes, use the shapePath property
      if (obj.shapePath) {
        shapeElement = `<g transform="translate(${left + width / 2}, ${
          top + height / 2
        })"><path d="${
          obj.shapePath
        }" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/></g>`;
      }
      break;
    }
    case 'text': {
      // For text shapes, we don't render a background shape
      break;
    }
    default: {
      // Default to rounded rectangle
      shapeElement = `<rect x="${left}" y="${top}" width="${width}" height="${height}" rx="8" ry="8" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    }
  }

  // Add text if present
  if (textContent && textContent.trim()) {
    const textX = left + width / 2;
    const textY = top + height / 2;
    // Extract color from HTML-style text content or use default
    let textColor = '#000000';
    const colorMatch = textContent.match(/color:\s*([^;"]+)/);
    if (colorMatch) {
      textColor = colorMatch[1];
    }
    // Extract plain text content
    const plainText = textContent.replace(/<[^>]*>/g, '').trim();
    textElement = `<text x="${textX}" y="${textY}" text-anchor="middle" dominant-baseline="central" fill="${textColor}" font-family="Arial, sans-serif" font-size="12">${plainText}</text>`;
  }

  return shapeElement + textElement;
};

// Calculate connection point on whiteboard shape perimeter
const getWhiteboardConnectionPoint = (
  obj: any,
  attachmentPoint: string
): { x: number; y: number } => {
  const { left, top, width, height } = obj;
  const centerX = left + width / 2;
  const centerY = top + height / 2;

  switch (attachmentPoint) {
    case 'ct': {
      return { x: centerX, y: top };
    }
    case 'cb': {
      return { x: centerX, y: top + height };
    }
    case 'cl': {
      return { x: left, y: centerY };
    }
    case 'cr': {
      return { x: left + width, y: centerY };
    }
    case 'cc':
    default: {
      return { x: centerX, y: centerY };
    }
  }
};

// Render whiteboard connector to SVG
const renderWhiteboardConnector = (
  obj: any,
  allObjects: Record<string, any>,
  offsetX: number,
  offsetY: number
): string => {
  const { stroke, strokeWidth, attachments, endStyles } = obj;

  if (!attachments || attachments.length < 2) {
    return '';
  }

  const fromObj = allObjects[attachments[0].objectId];
  const toObj = allObjects[attachments[1].objectId];

  if (!fromObj || !toObj) {
    return '';
  }

  const fromPoint = getWhiteboardConnectionPoint(
    fromObj,
    attachments[0].attachmentPoint
  );
  const toPoint = getWhiteboardConnectionPoint(
    toObj,
    attachments[1].attachmentPoint
  );

  // Adjust points with offset
  const adjustedFromPoint = {
    x: fromPoint.x + offsetX,
    y: fromPoint.y + offsetY,
  };
  const adjustedToPoint = { x: toPoint.x + offsetX, y: toPoint.y + offsetY };

  // Create a simple straight line for now
  let pathElement = `<line x1="${adjustedFromPoint.x}" y1="${adjustedFromPoint.y}" x2="${adjustedToPoint.x}" y2="${adjustedToPoint.y}" stroke="${stroke}" stroke-width="${strokeWidth}" color="${stroke}" />`;

  // Add markers for arrow heads if specified
  if (endStyles && endStyles.length >= 2) {
    if (endStyles[0] !== 0) {
      const markerType = getConnectorEndStyleSvg(endStyles[0]);
      pathElement = pathElement.replace(
        '<line',
        `<line marker-start="url(#${markerType})"`
      );
    }
    if (endStyles[1] !== 0) {
      const markerType = getConnectorEndStyleSvg(endStyles[1]);
      pathElement = pathElement.replace(
        '<line',
        `<line marker-end="url(#${markerType})"`
      );
    }
  }

  return pathElement;
};

// Convert whiteboard format to SVG
export const renderWhiteboardToSvg = (whiteboardData: any): string => {
  if (!whiteboardData || !whiteboardData.objects) {
    return '<svg></svg>';
  }

  const objects = whiteboardData.objects;

  // Calculate diagram bounds from objects
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const obj of Object.values(objects)) {
    const typedObj = obj as any;
    if (typedObj.type === 'shape') {
      minX = Math.min(minX, typedObj.left ?? Infinity);
      minY = Math.min(minY, typedObj.top ?? Infinity);
      maxX = Math.max(
        maxX,
        (typedObj.left ?? -Infinity) + (typedObj.width ?? 0)
      );
      maxY = Math.max(
        maxY,
        (typedObj.top ?? -Infinity) + (typedObj.height ?? 0)
      );
    }
  }

  // Add some padding
  const padding = 20;
  const width = maxX - minX + 2 * padding;
  const height = maxY - minY + 2 * padding;
  const offsetX = -minX + padding;
  const offsetY = -minY + padding;

  const markers = generateSvgMarkers();

  let content = '';

  // Create object lookup for connectors
  const objectsById: Record<string, any> = {};
  for (const obj of Object.values(objects)) {
    const typedObj = obj as any;
    if (typedObj.id) {
      objectsById[typedObj.id] = typedObj;
    }
  }

  // First render all shapes
  for (const obj of Object.values(objects)) {
    const typedObj = obj as any;
    if (typedObj.type === 'shape') {
      const adjustedObj = {
        ...typedObj,
        left: typedObj.left + offsetX,
        top: typedObj.top + offsetY,
      };
      content += renderWhiteboardShape(adjustedObj);
    }
  }

  // Then render connectors on top
  for (const obj of Object.values(objects)) {
    const typedObj = obj as any;
    if (typedObj.type === 'connector') {
      content += renderWhiteboardConnector(
        typedObj,
        objectsById,
        offsetX,
        offsetY
      );
    }
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
  ${markers}
  ${content}
</svg>`;
};
